export const focusAreas: Record<string, any> = {
  roadmapPlanning: {
    title: "Strategic AI Roadmap Development",
    description:
      "Create a clear, actionable AI roadmap that guides your organization from initial exploration to full-scale AI transformation with defined milestones and measurable outcomes.",
    symptoms: [
      "AI initiatives lack direction and clear priorities",
      "Projects start and stop without completing objectives",
      "No timeline or milestones for AI adoption",
      "Unclear what AI capabilities to build first",
    ],
    impacts: [
      "Wasted resources on AI projects that don't deliver value",
      "Slow progress due to lack of strategic direction",
      "Team confusion about priorities and goals",
      "Falling behind competitors with clear AI strategies",
    ],
    solutions: [
      "Develop a phased AI roadmap aligned with business growth stages",
      "Create clear milestones with success criteria for each phase",
      "Prioritize AI use cases based on impact and feasibility",
      "Establish governance framework for AI initiative management",
    ],
    nextSteps: [
      {
        step: "Assess Current State",
        action:
          "Conduct comprehensive audit of existing AI capabilities, data readiness, and skill gaps.",
      },
      {
        step: "Define Vision & Goals",
        action:
          "Articulate clear AI vision aligned with 3-5 year business strategy and specific objectives.",
      },
      {
        step: "Build Phased Roadmap",
        action:
          "Create detailed 12-18 month roadmap with quick wins, foundational builds, and transformation initiatives.",
      },
      {
        step: "Establish Governance",
        action:
          "Set up AI steering committee, decision frameworks, and regular review cadence.",
      },
    ],
    insight:
      "Organizations with documented AI roadmaps are 3x more likely to achieve their business objectives and see 40% faster time-to-value than those pursuing ad-hoc initiatives.",
  },
  businessAlignment: {
    title: "Business-Aligned AI Strategy",
    description:
      "Ensure every AI initiative directly supports your business objectives with clear ROI, bridging the gap between technology possibilities and business value creation.",
    symptoms: [
      "AI projects chosen based on technology trends, not business needs",
      "Difficulty articulating business value of AI investments",
      "Disconnect between AI team and business stakeholders",
      "AI success measured in technical metrics, not business outcomes",
    ],
    impacts: [
      "Low ROI on AI investments due to misaligned priorities",
      "Stakeholder skepticism and resistance to AI adoption",
      "Missed opportunities to solve critical business problems",
      "AI initiatives that don't move the needle on revenue or efficiency",
    ],
    solutions: [
      "Map AI capabilities directly to strategic business objectives and KPIs",
      "Create business case framework requiring clear ROI for each AI initiative",
      "Establish cross-functional teams bridging AI and business units",
      "Implement value tracking that connects AI outputs to business outcomes",
    ],
    nextSteps: [
      {
        step: "Identify Business Priorities",
        action:
          "Work with leadership to document top 5 business challenges and growth opportunities.",
      },
      {
        step: "Map AI to Business Value",
        action:
          "For each priority, identify specific AI applications that directly address the challenge.",
      },
      {
        step: "Build Business Cases",
        action:
          "Develop ROI models showing expected business impact, investment required, and timeline.",
      },
      {
        step: "Create Feedback Loops",
        action:
          "Establish regular business value reviews with metrics tied to company KPIs.",
      },
    ],
    insight:
      "Companies that tie AI initiatives to specific business KPIs achieve 60% higher success rates and demonstrate measurable ROI within 6 months versus 18+ months for technology-first approaches.",
  },
  implementationReadiness: {
    title: "AI Implementation Readiness",
    description:
      "Build the foundational capabilities - data infrastructure, technical skills, organizational culture, and change management - required to successfully implement and scale AI solutions.",
    symptoms: [
      "Data quality and accessibility issues blocking AI projects",
      "Lack of in-house AI expertise and talent",
      "Legacy systems incompatible with modern AI tools",
      "Cultural resistance to AI-driven changes",
    ],
    impacts: [
      "AI projects fail due to poor data or technical foundations",
      "Over-reliance on expensive external consultants",
      "Slow implementation and scaling of AI solutions",
      "Employee anxiety and resistance hindering adoption",
    ],
    solutions: [
      "Establish data governance and quality improvement programs",
      "Develop AI talent through training, hiring, and strategic partnerships",
      "Modernize technology stack to support AI workloads and integration",
      "Launch comprehensive change management and upskilling initiatives",
    ],
    nextSteps: [
      {
        step: "Data Readiness Assessment",
        action:
          "Audit data quality, accessibility, and governance across key business systems.",
      },
      {
        step: "Skills Gap Analysis",
        action:
          "Identify AI competency gaps and create development plan mixing training, hiring, and partnerships.",
      },
      {
        step: "Infrastructure Evaluation",
        action:
          "Assess technology stack readiness and plan upgrades or cloud migration as needed.",
      },
      {
        step: "Change Management Program",
        action:
          "Design communication, training, and support programs to drive AI adoption and reduce resistance.",
      },
    ],
    insight:
      "Organizations that invest in foundational readiness before launching AI initiatives see 75% higher implementation success rates and 50% faster scaling compared to those that skip this critical step.",
  },
};
